import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import { H3, Span, Text } from '@/components/atoms'
import { FileDocumentIcon } from '@/components/atoms/icons/file'
import { TrashEmptyIcon } from '@/components/atoms/icons/interface'
import {
  FileUpload,
  InfoBanner,
  SelectedFileList,
  SignaturePad,
} from '@/components/molecules'
import type { ClaimsEditPageState } from '../page.claims-edit.hook'

interface ClaimsEditStepTwoPageProps {
  page: ClaimsEditPageState
}

export function ClaimsEditStepTwoPage({ page }: ClaimsEditStepTwoPageProps) {
  const { t } = useTranslation()
  const { control, rules, fieldErrors } = page

  return (
    <div className="flex flex-col gap-6">
      <H3 className="text-[1.5rem] text-neutral-900">
        {t('claims.pet.stepTwo.title')}
      </H3>

      <Controller
        control={control}
        name="newInvoices"
        rules={rules.newInvoices}
        render={({ field }) => (
          <div className="flex flex-col gap-3">
            <FileUpload
              label={t('claims.pet.form.receiptLabel')}
              variant="document"
              multiple
              error={fieldErrors.invoices}
              onFilesSelect={page.addNewInvoices}
            />
            {page.keptInvoices.length > 0 && (
              <ul className="flex flex-col gap-2">
                {page.keptInvoices.map((invoice) => (
                  <li
                    key={invoice.id}
                    className="flex items-center gap-3 rounded-lg border border-neutral-100 bg-white px-4 py-3"
                  >
                    <FileDocumentIcon className="size-5 shrink-0 text-neutral-600" />
                    <Span className="flex-1 truncate text-neutral-900">
                      {invoice.file_name}
                    </Span>
                    <button
                      type="button"
                      aria-label={t('common.fileUpload.remove')}
                      onClick={() => page.removeKeptInvoice(invoice.id)}
                      className="hover:text-error-base text-neutral-600 transition"
                    >
                      <TrashEmptyIcon className="size-5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <SelectedFileList
              files={field.value}
              onRemove={page.removeNewInvoice}
            />
          </div>
        )}
      />

      <Controller
        control={control}
        name="petPicture"
        rules={rules.petPicture}
        render={() => (
          <FileUpload
            label={t('claims.pet.form.petPictureLabel')}
            previewUrl={page.petPicturePreviewUrl}
            selectedFileName={page.currentPetPictureName}
            error={fieldErrors.petPicture}
            onFileSelect={page.selectPetPicture}
          />
        )}
      />

      {!page.hasPledge && (
        <div className="flex flex-col gap-3">
          <Text className="font-bold text-neutral-900">
            {t('claims.pet.form.pledgeTitle')}
          </Text>
          <Text className="text-sm text-neutral-600">
            {t('claims.pet.form.pledgeDescription')}
          </Text>
          <Controller
            control={control}
            name="signature"
            rules={rules.signature}
            render={({ field }) => (
              <SignaturePad
                label={t('claims.pet.form.signatureLabel')}
                error={fieldErrors.signature}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      )}

      <InfoBanner message={t('claims.pet.form.reimbursementInfo')} />

      {page.submitErrorKey && (
        <div role="alert" className="alert alert-error">
          <span>{t(page.submitErrorKey)}</span>
        </div>
      )}

      {page.isOffline && (
        <Text className="text-indigo-base !text-sm">
          {t('payment.offlineError')}
        </Text>
      )}
    </div>
  )
}
